import {
  DestinationPropertyFragment,
  SourcePropertyFragment,
} from "@/apollo/types";

import { PropertyMappingFormValue } from "../form";

/**
 * Generates field mappings for a reverse ETL sync based on the provided
 * initial mappings, auto‐mapped properties, and required destination
 * properties.
 */
export const generateFieldMappings = (params: {
  initialMappings?: PropertyMappingFormValue[];
  autoMappedProperties: {
    sourceProperty: SourcePropertyFragment;
    destinationProperty: DestinationPropertyFragment;
  }[];
  requiredDestinationProperties: DestinationPropertyFragment[];
}) => {
  const requiredPropertiesMap = new Map(
    params.requiredDestinationProperties.map((x) => [x.propertyId, x]),
  );

  // 1. Start with the initial mappings
  const mappings: PropertyMappingFormValue[] = (params.initialMappings ?? [])
    // Remove empty mappings
    .filter((item) => {
      const isEmpty = !item.sourceProperty && !item.destinationProperty;
      return !isEmpty;
    })
    // Remove any mappings that have been auto‐mapped
    .filter((item) => {
      const isSourceAutoMapped = params.autoMappedProperties.some(
        (autoMapped) =>
          autoMapped.sourceProperty.name === item.sourceProperty?.name,
      );
      const isDestinationAutoMapped = params.autoMappedProperties.some(
        (autoMapped) =>
          autoMapped.destinationProperty.propertyId ===
          item.destinationProperty?.value,
      );
      return !isSourceAutoMapped && !isDestinationAutoMapped;
    });

  // 2. Add the auto‐mapped properties
  for (const item of params.autoMappedProperties) {
    const destId = item.destinationProperty.propertyId;
    const isRequired = requiredPropertiesMap.has(destId);
    mappings.push({
      destinationProperty: {
        value: destId,
        label: item.destinationProperty.propertyName,
        meta: item.destinationProperty,
      },
      isRequired,
      sourceProperty: item.sourceProperty,
    });
  }

  // 3. Add any required destination properties not yet mapped
  for (const item of requiredPropertiesMap.values()) {
    if (
      mappings.some((x) => x.destinationProperty?.value === item.propertyId)
    ) {
      continue;
    }
    mappings.push({
      destinationProperty: {
        value: item.propertyId,
        label: item.propertyName,
        meta: item,
      },
      isRequired: true,
      sourceProperty: null,
    });
  }

  const primaryMappingCandidate = mappings.find(
    (x) =>
      x.destinationProperty?.meta?.isPrimaryKey === true &&
      x.isRequired === true,
  );

  if (primaryMappingCandidate) {
    const primaryMappingIndex = mappings.indexOf(primaryMappingCandidate);
    // remove the primary mapping from the field `mappings` array
    mappings.splice(primaryMappingIndex, 1);
  }

  return {
    primaryMapping: primaryMappingCandidate,
    mappings: mappings.sort((a, b) => {
      const isARequired = !!a.destinationProperty?.meta?.isRequired;
      const isBRequired = !!b.destinationProperty?.meta?.isRequired;
      return isARequired === isBRequired ? 0 : isARequired ? -1 : 1;
    }),
  };
};
